import * as Sentry from '@sentry/vue'

// Unlike the backend, a bad rate disables tracing instead of throwing: nothing
// gates a frontend deploy, so a throw here would blank the app.
function parseTraceSampleRate(raw) {
    if (raw === undefined || raw === null || String(raw).trim() === '') {
        return 0
    }
    const rate = Number(raw)
    if (!Number.isFinite(rate) || rate < 0 || rate > 1) {
        console.warn(`VITE_SENTRY_TRACE_SAMPLE_RATE=${raw} is not a fraction between 0 and 1; tracing is off.`)
        return 0
    }
    return rate
}

// The two URLs that carry a single-use login credential: the page a native app
// loads to sign its user in, and the exchange that page makes. A successful
// exchange spends the token, but one that never reached the server leaves it
// good until it expires, and Sentry records the page URL and every request URL
// without being asked. The Expo app redacts the same thing for the same reason
// (its src/auth/handover.ts).
const ONE_TIME_TOKEN_URL = /(\/login\/(?:auto|complete\/get-session)\/)[^/\s?#]+\/[^/\s?#]+/g

// Deep enough for anything Sentry builds; a bound so that a value which
// refers to itself cannot recurse for ever.
const REDACT_MAX_DEPTH = 12

// Redacts in place and returns what it was given, so beforeSend still hands
// back the event it received. Only a string that held a token is written back,
// so nothing else in the event is touched.
function redactOneTimeTokens(value, depth = 0) {
    if (typeof value === 'string') {
        return value.replace(ONE_TIME_TOKEN_URL, '$1<redacted>')
    }
    if (value === null || typeof value !== 'object' || depth >= REDACT_MAX_DEPTH) {
        return value
    }
    for (const key of Object.keys(value)) {
        const redacted = redactOneTimeTokens(value[key], depth + 1)
        if (redacted !== value[key]) {
            value[key] = redacted
        }
    }
    return value
}

// sdkProcessingMetadata is dropped before the event is sent, and on a
// transaction it holds the live scope: from there the walk reaches the client,
// the Vue app and the router, and rewrites the route the user is on.
function redactEvent(event) {
    for (const key of Object.keys(event)) {
        if (key !== 'sdkProcessingMetadata') {
            event[key] = redactOneTimeTokens(event[key], 1)
        }
    }
    return event
}

export function buildSentryOptions(env, { app, router }) {
    if (!env.VITE_SENTRY_DSN) {
        return null
    }

    const options = {
        app,
        dsn: env.VITE_SENTRY_DSN,
        environment: env.VITE_DELTA_IS_PRODUCTION ? 'production' : env.VITE_DELTA_ENV_CODE,
        ignoreErrors: [
            "Rendering cancelled",
            // Transient client-side network failures - not actionable bugs
            "Load failed",
            "Failed to fetch",
            "NetworkError when attempting to fetch resource",
            // Noise from browser extensions' internal RPC bridge, surfaced
            // via the page's global onunhandledrejection handler. Not our code.
            "Object Not Found Matching Id",
            // A synthetic `unhandledrejection` CustomEvent dispatched by injected
            // extension code, carrying neither `reason` nor `detail.reason`. With
            // nothing to unwrap, Sentry's global handler captures the bare event,
            // so the report has no stack and no reason to act on. Never ours:
            // Safari fires a native PromiseRejectionEvent (which always carries
            // `reason`), and nothing we bundle dispatches a CustomEvent.
            "(type=unhandledrejection) captured as promise rejection",
        ],
        beforeSend(event, hint) {
            // Drop our own silent-401 rejections (api.js handleResponse). A 401
            // has exactly one UX — the logged-out modal or the login redirect —
            // and is flagged __silent__ so callers don't toast it. When such a
            // rejection escapes an uncaught/fire-and-forget promise it reaches
            // window.onunhandledrejection and Sentry would report it anyway;
            // honor the flag here so the reporting boundary stays silent too.
            const ex = hint && hint.originalException
            if (ex && (ex.__silent__ || ex.__status__ === 401)) {
                return null
            }
            return redactEvent(event)
        },
        beforeSendTransaction(event) {
            return redactEvent(event)
        },
        integrations: [],
    }

    const tracesSampleRate = parseTraceSampleRate(env.VITE_SENTRY_TRACE_SAMPLE_RATE)
    if (tracesSampleRate > 0) {
        // BrowserTracing is what adds sentry-trace/baggage to API requests, which
        // the API's CORS_ALLOW_HEADERS must permit.
        options.integrations.push(Sentry.browserTracingIntegration({ router }))
        options.tracesSampleRate = tracesSampleRate
        options.tracePropagationTargets = [env.VITE_API_URL || 'localhost']
    }

    return options
}
